import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { pageTransitionTo } from "./PageTransition";
// MobileAccountMenuItem intentionally not imported — the cart + profile
// icons live in the global ShopHeaderCluster (top-right of every non-studio
// page) so we don't duplicate them inside the slide-out menu.
import { GUTTER, MENU_SECTIONS, MENU_HIGHLIGHT, LEGAL_LINKS, SOCIALS } from "./NewSiteConstants";

const MOBILE_BP = 1000;
const MOBILE_GUTTER = 20;
const PANEL_W = 400;

/**
 * Floating burger trigger + fade-in white menu overlay.
 * No fixed header bar — the trigger just floats in the top-left corner.
 * Menu fades opacity 0→1 over 1s (and 1→0 over 1s on close).
 * Clicking outside the panel (on the dimmed frost) also closes it.
 */
export default function NewSiteHeader({ triggerColor = "#fff", side = "left", blendMode = true }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [rendered, setRendered] = useState(false); // DOM presence
  const [visible, setVisible] = useState(false);   // opacity trigger (one frame after mount)
  const [burgerHover, setBurgerHover] = useState(false);
  const [hoveredItem, setHoveredItem] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BP);
  const [scrolled, setScrolled] = useState(false);

  // Fade the mobile top-bar backdrop in/out based on scroll position.
  // Some pages (About, Help, Studio landings…) put their content in a
  // nested `overflow: auto` container instead of scrolling the window,
  // so a plain `window.scroll` listener misses them. Capture ALL scroll
  // events at the document level — scroll doesn't bubble, but the
  // capture phase catches them from every scrollable child. Then flip
  // `scrolled` on when the actual target's scrollTop passes 12px.
  useEffect(() => {
    const check = (target) => {
      // window / document → use window.scrollY. Element → its scrollTop.
      const y = target && target.scrollTop !== undefined && target !== document
        ? target.scrollTop
        : window.scrollY || document.documentElement.scrollTop || 0;
      setScrolled(y > 12);
    };
    const onWin = () => check(null);
    const onCap = (e) => check(e.target);
    onWin();
    window.addEventListener("scroll", onWin, { passive: true });
    document.addEventListener("scroll", onCap, { capture: true, passive: true });
    return () => {
      window.removeEventListener("scroll", onWin);
      document.removeEventListener("scroll", onCap, { capture: true });
    };
  }, []);

  /**
   * Two-phase mount/fade so opacity actually transitions on OPEN:
   *   - Open: mount with opacity 0 → next frame set opacity 1 → CSS transitions from 0 to 1 over 1s.
   *   - Close: set opacity 0 → after 1s, unmount.
   */
  useEffect(() => {
    if (menuOpen) {
      setRendered(true);
      // Two rAFs guarantees the initial 0-opacity paint has committed before we flip to 1.
      const r1 = requestAnimationFrame(() => {
        const r2 = requestAnimationFrame(() => setVisible(true));
        return () => cancelAnimationFrame(r2);
      });
      return () => cancelAnimationFrame(r1);
    } else if (rendered) {
      setVisible(false);
      const t = setTimeout(() => setRendered(false), 1000);
      return () => clearTimeout(t);
    }
  }, [menuOpen, rendered]);

  const navigateWithTransition = useCallback((path) => {
    // Close the menu immediately, then hand off to the global PageTransition
    // overlay. Falls back to a direct navigate if the overlay isn't mounted
    // for any reason so links can never end up dead.
    setMenuOpen(false);
    pageTransitionTo(path, navigate);
  }, [navigate]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < MOBILE_BP);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const gutter = isMobile ? MOBILE_GUTTER : GUTTER;
  const panelWidth = isMobile ? "100vw" : PANEL_W;

  // Burger → X animated icon (1s transition).
  // On mobile the burger + label are scaled up ~25% for better tap target visibility.
  const burgerW = isMobile ? 28 : 22;
  const burgerH = isMobile ? 20 : 16;
  const burgerLineH = isMobile ? 2 : 1.6;
  const burgerTop1 = isMobile ? 4 : 3;
  const burgerTop2 = isMobile ? 9 : 7;
  const burgerTop3 = isMobile ? 13 : 10;
  const burgerIcon = (
    <div style={{ width: burgerW, height: burgerH, position: "relative" }} data-testid="burger-icon">
      <span style={{
        position: "absolute", left: 0, height: burgerLineH, borderRadius: 1,
        background: menuOpen ? "#111" : triggerColor,
        transition: "top 1s cubic-bezier(0.4,0,0.2,1), transform 1s cubic-bezier(0.4,0,0.2,1), background 0.6s ease",
        width: burgerW,
        top: menuOpen ? burgerTop2 : burgerTop1,
        transform: menuOpen ? "rotate(45deg)" : "rotate(0deg)",
        transformOrigin: "50% 50%",
      }} />
      <span style={{
        position: "absolute", left: 0, height: burgerLineH, borderRadius: 1,
        background: menuOpen ? "#111" : triggerColor,
        transition: "top 1s cubic-bezier(0.4,0,0.2,1), transform 1s cubic-bezier(0.4,0,0.2,1), width 1s cubic-bezier(0.4,0,0.2,1), background 0.6s ease",
        width: menuOpen ? burgerW : (burgerHover ? burgerW : Math.round(burgerW * 0.64)),
        top: menuOpen ? burgerTop2 : burgerTop3,
        transform: menuOpen ? "rotate(-45deg)" : "rotate(0deg)",
        transformOrigin: "50% 50%",
      }} />
    </div>
  );

  return (
    <>
      {/* Mobile top-bar backdrop — soft off-white strip behind the MENU
          burger, cart cluster and F logo. Fades IN when the user scrolls
          past 12px, fades back OUT when they return to the top. Stays
          visible while the menu opens/closes if the user is scrolled —
          the menu overlay animates on top, so the backdrop shouldn't
          pop away underneath it. */}
      {isMobile && (
        <div
          aria-hidden
          data-testid="mobile-top-bar-backdrop"
          style={{
            position: "fixed", top: 0, left: 0, right: 0, height: 88,
            background: "#f7f7f8",
            borderBottom: "1px solid rgba(0,0,0,0.06)",
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            zIndex: 30,
            pointerEvents: "none",
            opacity: scrolled ? 1 : 0,
            transition: "opacity 0.5s ease",
          }}
        />
      )}

      {/* ═══════ FLOATING BURGER / CLOSE CHIP ═══════
          Single element: transitions between "MENU" burger and "X CLOSE" white chip
          over a 1s animation. Uses mix-blend-mode:difference when closed so the burger
          auto-inverts against whatever background is beneath it (dark vs light regions). */}
      <div
        data-testid="burger-trigger"
        onClick={() => {
          setMenuOpen(o => {
            const next = !o;
            // When opening the menu, ensure Flux drawer closes so the menu
            // supersedes it (menu wins z-order UX).
            if (next) window.dispatchEvent(new Event("lf:close-flux"));
            return next;
          });
        }}
        onMouseEnter={() => setBurgerHover(true)}
        onMouseLeave={() => setBurgerHover(false)}
        style={{
          position: "fixed",
          // Menu burger is ~20px tall; cart / F-logo are 48px tall and sit at
          // top:20 on mobile (center Y = 44). Anchor the burger's top so its
          // vertical centre lines up with the cart + logo on that same
          // imaginary horizontal line.
          top: menuOpen ? 20 : (isMobile ? 34 : 20),
          ...(side === "right"
            ? { right: menuOpen ? (isMobile ? MOBILE_GUTTER : 28) : gutter }
            : { left: menuOpen ? (isMobile ? MOBILE_GUTTER : 28) : gutter }),
          display: "inline-flex", alignItems: "center",
          flexDirection: side === "right" ? "row-reverse" : "row",
          gap: menuOpen ? 8 : 12,
          padding: menuOpen ? "7px 13px" : "0",
          background: menuOpen ? "#ffffff" : "rgba(255,255,255,0)",
          boxShadow: menuOpen
            ? "0 6px 18px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.10)"
            : "none",
          borderRadius: 4,
          cursor: "pointer", userSelect: "none",
          zIndex: 70,
          mixBlendMode: menuOpen ? "normal" : (blendMode ? "difference" : "normal"),
          // Delay the mix-blend swap by 1s on close so the white chip fades out cleanly BEFORE
          // the burger's blend mode kicks in (otherwise the chip momentarily inverts to black).
          transition: "left 1s cubic-bezier(0.4,0,0.2,1), right 1s cubic-bezier(0.4,0,0.2,1), padding 1s cubic-bezier(0.4,0,0.2,1), background 1s cubic-bezier(0.4,0,0.2,1), box-shadow 1s cubic-bezier(0.4,0,0.2,1), gap 1s cubic-bezier(0.4,0,0.2,1), mix-blend-mode 0s " + (menuOpen ? "0s" : "1s"),
        }}
      >
        {burgerIcon}
        <span style={{
          fontSize: menuOpen ? "0.66rem" : (isMobile ? "0.9rem" : "0.72rem"), fontWeight: 400,
          color: menuOpen ? "#111" : triggerColor,
          letterSpacing: "0.24em", textTransform: "uppercase",
          transition: "color 0.6s ease, transform 0.4s ease, font-size 0.6s ease",
          transform: burgerHover ? (side === "right" ? "translateX(-3px)" : "translateX(3px)") : "translateX(0)",
        }}>{menuOpen ? "Close" : "Menu"}</span>
      </div>

      {/* ═══════ MENU (fade in-place, no slide) ═══════ */}
      {rendered && (
        <>
          {/* Frosted backdrop — click to close */}
          <div
            data-testid="menu-backdrop"
            onClick={() => setMenuOpen(false)}
            style={{
              position: "fixed", inset: 0, zIndex: 55,
              background: "rgba(0,0,0,0.35)",
              backdropFilter: "blur(10px)",
              WebkitBackdropFilter: "blur(10px)",
              opacity: visible ? 1 : 0,
              transition: "opacity 1s cubic-bezier(0.4,0,0.2,1)",
              cursor: "pointer",
            }}
          />

          {/* White menu panel — fades opacity 0→1 in place over 1s */}
          <aside
            data-testid="menu-overlay"
            style={{
              position: "fixed", top: 0, bottom: 0,
              ...(side === "right" ? { right: 0 } : { left: 0 }),
              width: panelWidth,
              zIndex: 60,
              background: "#ffffff",
              boxShadow: visible ? "0 12px 60px rgba(0,0,0,0.35)" : "none",
              opacity: visible ? 1 : 0,
              pointerEvents: visible ? "auto" : "none",
              transition: "opacity 1s cubic-bezier(0.4,0,0.2,1), box-shadow 1s ease",
              display: "flex", flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* ─── Fixed top: F logo + LumenForge-MC + BUILD · CUT · SHIP + divider line ─── */}
            <div
              data-testid="menu-brand"
              onClick={() => { navigateWithTransition("/"); }}
              style={{
                flexShrink: 0,
                padding: `74px ${isMobile ? MOBILE_GUTTER : 26}px 18px`,
                background: "#ffffff",
                display: "flex", flexDirection: "column", alignItems: "center",
                textAlign: "center",
                zIndex: 2,
                cursor: "pointer",
                transition: "opacity 0.3s ease",
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = "0.85"}
              onMouseLeave={e => e.currentTarget.style.opacity = "1"}
            >
              <img
                src="/lumenforge-f-logo.webp"
                alt="LumenForge-MC"
                style={{ width: 52, height: 52, objectFit: "contain", marginBottom: 12 }}
              />
              <div style={{
                fontSize: "1.25rem", fontWeight: 500, color: "#111",
                letterSpacing: "0.005em",
              }}>LumenForge-MC</div>
              <div style={{
                fontSize: "0.56rem", color: "rgba(0,0,0,0.42)",
                letterSpacing: "0.42em", textTransform: "uppercase",
                marginTop: 8,
              }}>Build · Cut · Ship</div>
              {/* Divider line — 70% width, 15% margin each side */}
              <div style={{
                width: "70%",
                height: 1,
                background: "rgba(0,0,0,0.12)",
                marginTop: 20,
              }} />
            </div>

            {/* ─── Scrollable middle: sections (Product scrolls behind the fixed brand) ─── */}
            <div
              className="hide-scrollbar-menu"
              style={{
                flex: 1,
                overflowY: "auto", overflowX: "hidden",
                padding: `10px ${isMobile ? MOBILE_GUTTER : 22}px 26px`,
              }}
            >
              {/* Highlight CTA — solid pill, centrally aligned in its container. */}
              {MENU_HIGHLIGHT && (() => {
                const BRAND_BLUE = "#3b82f6";
                return (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 22 }}>
                <button
                  data-testid="menu-highlight-cta"
                  onClick={() => {
                    setMenuOpen(false);
                    pageTransitionTo(MENU_HIGHLIGHT.path, navigate);
                  }}
                  style={{
                    padding: "10px 20px",
                    borderRadius: 4,
                    border: `1px solid ${BRAND_BLUE}`,
                    background: BRAND_BLUE,
                    color: "#fff",
                    cursor: "pointer",
                    fontFamily: "inherit",
                    display: "inline-flex", alignItems: "center", gap: 10,
                    fontSize: "0.72rem", fontWeight: 700,
                    letterSpacing: "0.1em", textTransform: "uppercase",
                    transition: "background 0.25s ease, transform 0.2s ease",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#2563eb"; e.currentTarget.style.transform = "translateY(-1px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = BRAND_BLUE; e.currentTarget.style.transform = "translateY(0)"; }}
                >
                  {MENU_HIGHLIGHT.label}
                  <span style={{ fontSize: "0.95rem", lineHeight: 1, fontWeight: 400 }}>+</span>
                </button>
                </div>
                );
              })()}

              {MENU_SECTIONS.map((section, sIdx) => (
                <div key={section.heading} style={{ marginTop: sIdx === 0 ? 26 : 0 }}>
                  {/* Between-section soft divider — same style as the brand block divider */}
                  {sIdx > 0 && (
                    <div style={{
                      display: "flex", justifyContent: "center",
                      margin: "28px 0",
                    }}>
                      <div style={{
                        width: "70%", height: 1,
                        background: "rgba(0,0,0,0.10)",
                      }} />
                    </div>
                  )}

                  {/* Centered bold uppercase heading */}
                  <div style={{
                    fontSize: "0.9rem", fontWeight: 700,
                    color: "#111",
                    letterSpacing: "0.24em", textTransform: "uppercase",
                    padding: "0 4px 16px",
                    textAlign: "center",
                  }}>{section.heading}</div>

                  {section.items.map((item) => {
                    const isActive = item.path && location.pathname === item.path;
                    const hoverKey = `${section.heading}-${item.label}`;
                    const isHovered = hoveredItem === hoverKey;

                    // Look up matching social icon by label when in the Socials section
                    const socialMatch = section.heading === "Socials"
                      ? SOCIALS.find(s => s.label.toLowerCase() === item.label.toLowerCase())
                      : null;
                    const SocialIcon = socialMatch?.Icon;
                    const socialBrandColor = socialMatch ? ({
                      youtube: "#FF0000",
                      patreon: "#F96854",
                      tiktok: "#000000",
                      instagram: "#E4405F",
                    }[socialMatch.label.toLowerCase()] || "#111") : "#111";
                    return (
                      <div
                        key={item.label}
                        data-testid={`menu-item-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => {
                          if (item.path) navigateWithTransition(item.path);
                          else if (item.url) window.open(item.url, "_blank", "noopener,noreferrer");
                        }}
                        onMouseEnter={() => setHoveredItem(hoverKey)}
                        onMouseLeave={() => setHoveredItem(null)}
                        style={{
                          position: "relative",
                          display: "flex", alignItems: "center", justifyContent: "space-between",
                          gap: 12,
                          padding: "12px 14px",
                          cursor: "pointer",
                          borderRadius: 6,
                          background: isActive ? "rgba(0,0,0,0.06)" : isHovered ? "rgba(0,0,0,0.035)" : "transparent",
                          transition: "background 0.3s ease",
                        }}
                      >
                        {/* Left-side social icon (only in Socials section) */}
                        {SocialIcon && (
                          <div style={{
                            width: 30, height: 30, borderRadius: 6,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            background: "rgba(0,0,0,0.045)",
                            color: isHovered ? socialBrandColor : "#111",
                            flexShrink: 0,
                            transition: "background 1s ease, color 1s ease",
                          }}>
                            <SocialIcon size={15} />
                          </div>
                        )}
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{
                            fontSize: "1rem", fontWeight: 600,
                            color: "#111",
                            letterSpacing: "0.005em",
                          }}>{item.label}</div>
                          {item.sub && (
                            <div style={{
                              fontSize: "0.95rem", color: "rgba(0,0,0,0.55)",
                              marginTop: 4, letterSpacing: "0.005em",
                            }}>{item.sub}</div>
                          )}
                        </div>
                        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="rgba(0,0,0,0.35)" strokeWidth="1.4" style={{
                          transform: isHovered ? "translateX(3px)" : "translateX(0)",
                          transition: "transform 0.3s ease",
                          flexShrink: 0, marginLeft: 8,
                        }}>
                          <path d="M4 2 L8 6 L4 10" />
                        </svg>
                      </div>
                    );
                  })}
                </div>
              ))}

              {/* Shop cart + profile icons intentionally NOT rendered inside
                  the menu — they live in the global ShopHeaderCluster on the
                  top-right of every page (except the studio), so a duplicate
                  in the menu would be redundant. Kept the import path in
                  ShopHeaderCluster.js if we ever want to re-add for mobile. */}

              {/* Veteran-owned badge — bottom of the sections, above the
                  footer. Extra gap above so the shop rows breathe. */}
              <div data-testid="menu-veteran" style={{
                display: "flex", justifyContent: "center",
                marginTop: 40, marginBottom: 6,
              }}>
                <img
                  src="/veteran-owned.webp"
                  alt="Veteran Owned"
                  style={{
                    height: 34, objectFit: "contain",
                    filter: "brightness(0) opacity(0.7)",
                  }}
                />
              </div>
            </div>

            {/* ─── Fixed bottom footer — white, matches the top brand strip ─── */}
            {/* Soft divider above the footer (same style as the one under the brand block) */}
            <div style={{
              flexShrink: 0,
              display: "flex", justifyContent: "center",
              background: "#ffffff",
              paddingTop: 4,
            }}>
              <div style={{
                width: "70%", height: 1,
                background: "rgba(0,0,0,0.10)",
              }} />
            </div>
            <div style={{
              flexShrink: 0,
              background: "#ffffff",
              padding: `14px ${isMobile ? MOBILE_GUTTER : 26}px 16px`,
              display: "flex", alignItems: "center", justifyContent: "space-between",
              zIndex: 2,
            }}>
              <div style={{
                fontSize: "0.66rem", color: "rgba(0,0,0,0.55)",
                letterSpacing: "0.08em",
                display: "flex", alignItems: "center", gap: 6,
              }}>
                <span style={{ fontSize: "0.82rem", lineHeight: 1 }}>©</span>
                <span>Insid3 Th3 Cub3</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {LEGAL_LINKS.map((l, i, arr) => (
                  <span key={l.label} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span
                      onClick={() => navigateWithTransition(l.path)}
                      style={{
                        fontSize: "0.66rem", color: "rgba(0,0,0,0.55)",
                        cursor: "pointer", letterSpacing: "0.1em",
                        transition: "color 0.3s",
                      }}
                      onMouseEnter={e => e.currentTarget.style.color = "#111"}
                      onMouseLeave={e => e.currentTarget.style.color = "rgba(0,0,0,0.55)"}
                    >{l.label}</span>
                    {i < arr.length - 1 && (
                      <span style={{ fontSize: "0.66rem", color: "rgba(0,0,0,0.32)" }}>.</span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          </aside>
        </>
      )}

      {/* Menu-triggered fade is now handled by the global PageTransition overlay. */}

      <style>{`
        .hide-scrollbar-menu::-webkit-scrollbar { display: none; }
        .hide-scrollbar-menu { scrollbar-width: none; -ms-overflow-style: none; }
      `}</style>
    </>
  );
}
